// Local replacement for `decodeToken`, dropped from `@spazious/client-backoffice` in 4.117.0.
// Callers read the `exp`, `iat` and `user_id` claims and rely on this throwing for anything
// that is not a JWT (a legacy, pre-SimpleJWT token such as a 40-char hex key has no payload
// segment).
export const decodeToken = (token: string): Record<string, unknown> => {
  const payload = token.split('.')[1];
  if (!payload) {
    throw new Error('decodeToken: not a JWT (missing payload segment)');
  }

  const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  // `atob` yields one latin1 char per byte, so a multi-byte claim has to be re-assembled
  // and decoded as UTF-8 — `jwt-decode`, which this replaces, did the same.
  const bytes = Uint8Array.from(globalThis.atob(padded), (character) => character.charCodeAt(0));

  return JSON.parse(new TextDecoder().decode(bytes));
};

/**
 * Subject of an access token — SimpleJWT's `user_id` claim, i.e. the user the token was
 * minted for. During impersonation this stays the *real* admin: the backend resolves the
 * impersonated user from the active session, it does not re-mint the token.
 *
 * Returns `null` for a token that is not a decodable JWT or carries no usable claim.
 */
export const getTokenSubjectId = (token: string): string | null => {
  let userId: unknown;
  try {
    ({ user_id: userId } = decodeToken(token));
  } catch {
    return null;
  }

  if (typeof userId === 'number') {
    return String(userId);
  }

  return typeof userId === 'string' && userId !== '' ? userId : null;
};

export default decodeToken;
